import { useLatestFn } from '@studio/react-utils/hooks';
import { createHook } from '@studio/react-utils/store';
import type { T_Maybe, T_Scalar } from '@tryiris-ai/js-types';
import { isNil, isScalarType, toScalar } from '@tryiris-ai/js-types';
import { useMemo } from 'react';
import { preferences } from './preferences.js';
import type { Producer, Transformer, UsePrefState } from './types.js';
import { StudioError } from '#~/error/index.js';

/** Re-exports. */
export type { Transformer, UsePrefState };

/**
 * Hook to obtain the application preferences.
 *
 * @returns Application preferences.
 */
export const usePreferences = createHook(preferences);

/**
 * Hook to get and set an application preference value.
 *
 * @param key - Preference key.
 * @param defaultValue - Default preference value.
 * @returns Preference value and setter.
 */
export function usePrefState<T extends T_Scalar | undefined>(
  key: string,
  defaultValue: T
): UsePrefState<T>;

/**
 * Hook to get and set an application preference value.
 *
 * @param key - Preference key.
 * @param defaultValue - Default preference value.
 * @param transformer - Preference value transformer.
 * @returns Preference value and setter.
 */
export function usePrefState<T>(
  key: string,
  defaultValue: T,
  transformer: Transformer<T>
): UsePrefState<T>;

/** @internal */
export function usePrefState<T>(
  key: string,
  defaultValue: T,
  transformer?: Transformer<T>
): UsePrefState<T> {
  const { deserialize, serialize } = transformer ?? {};

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const producer = useProducer();
  const rawValue = usePreferences((state) => state.getValue(key));

  return [
    useMemo(() => {
      if (isNil(rawValue)) {
        return defaultValue;
      }

      if (deserialize) {
        return deserialize(rawValue, defaultValue) ?? defaultValue;
      }

      if (!isScalarType(rawValue)) {
        throw new StudioError('Missing preference value deserializer.');
      }

      return <T_Maybe<T>>toScalar(rawValue) ?? defaultValue;
    }, [deserialize, rawValue, defaultValue]),

    useLatestFn((nextValue: unknown) => {
      producer((draft) => {
        if (isNil(nextValue)) {
          draft.setValue(key, undefined);

          return;
        }

        if (serialize) {
          draft.setValue(key, serialize(nextValue));

          return;
        }

        if (!isScalarType(nextValue)) {
          throw new StudioError('Missing preference value serializer.');
        }

        draft.setValue(key, toScalar(nextValue));
      });
    })
  ];
}

/**
 * Hook to obtain the preferences (not reactive). This hook is wholly unnecessary as the producer is
 * a statically imported function, but React developers love their hooks.
 *
 * @returns Preferences producer.
 */
export const useProducer = (): Producer => preferences.getState().produce;
